// Utilidades puras para la cascada de permisos padre -> hijos (DBSB-458).
// Los permisos usan jerarquía por nombre separada por puntos:
//   "Nominas.Incidencias" (padre) -> "Nominas.Incidencias.Cargar" (hijo).

export interface PermisoItem {
  name?: string;
  status?: any;
}

// Permiso que gatea la propia pantalla "Usuarios y permisos" (MiEmpresaScreen).
// Si un admin se lo quita a SÍ MISMO, pierde el acceso al panel y no puede
// re-habilitarlo desde la UI (auto-bloqueo). Se protege al editar la cuenta propia.
export const PERMISO_ACCESO_ADMIN = "Colaboradores.Administradores.Acceder";

// Devuelve los nombres de los permisos descendientes de `parentName`, por prefijo
// jerárquico ("Padre." -> "Padre.Hijo", "Padre.Hijo.Nieto", ...). Genérico para
// todos los módulos: no depende de listas hardcodeadas por bloque.
//
// Caso especial del switch "principal" de un módulo: se llama "Sidebar.<Modulo>"
// (ej. "Sidebar.Colaboradores"), pero el árbol de permisos del módulo vive bajo
// "<Modulo>.*" (ej. "Colaboradores.NuevoIngreso.Agregar"). Para que el principal
// cascadee a TODO el módulo, se incluye también ese subárbol (DBSB-458).
export const getDescendantPermissionNames = (
  parentName: string,
  permisos: PermisoItem[] | any
): string[] => {
  if (!parentName || !Array.isArray(permisos)) return [];
  const prefijos = [`${parentName}.`];
  if (parentName.startsWith("Sidebar.")) {
    const modulo = parentName.slice("Sidebar.".length);
    if (modulo) prefijos.push(`${modulo}.`);
  }
  return permisos
    .filter(
      (p: any) =>
        typeof p?.name === "string" &&
        prefijos.some((pre) => p.name.startsWith(pre))
    )
    .map((p: any) => p.name as string);
};

// Nombre del padre inmediato de un permiso ("A.B.C" -> "A.B"). Cadena vacía si el
// permiso no tiene padre (nivel raíz o valor inválido).
export const getParentPermissionName = (name: string): string => {
  if (typeof name !== "string" || !name.includes(".")) return "";
  return name.split(".").slice(0, -1).join(".");
};

// Quita el permiso que gatea la pantalla del lote a apagar cuando el admin edita su
// PROPIA cuenta (anti-autobloqueo, DBSB-458). En cualquier otro caso deja la lista igual.
export const protegerAutobloqueo = (
  nombres: string[],
  opts: { apagando: boolean; editandoPropiaCuenta: boolean }
): string[] => {
  if (opts.apagando && opts.editandoPropiaCuenta) {
    return nombres.filter((n) => n !== PERMISO_ACCESO_ADMIN);
  }
  return nombres;
};

// Fusiona dos listas de permisos por nombre: parte de `base` y aplica/añade los de
// `updates` (a igual nombre gana `updates`). NUNCA elimina entradas de `base` que no
// estén en `updates` — así el refresco en caliente no puede "perder" un permiso
// (p.ej. el que gatea la pantalla de administración) y romper la app (DBSB-458).
export const mergePermisosByName = (
  base: PermisoItem[] | any,
  updates: PermisoItem[] | any
): PermisoItem[] => {
  const map = new Map<string, PermisoItem>();
  (Array.isArray(base) ? base : []).forEach((p: any) => {
    if (p && typeof p.name === "string") map.set(p.name, p);
  });
  (Array.isArray(updates) ? updates : []).forEach((p: any) => {
    if (p && typeof p.name === "string") {
      map.set(p.name, { ...(map.get(p.name) || {}), ...p });
    }
  });
  return Array.from(map.values());
};
